import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Buildings, MagnifyingGlass, Plus, Trash, Copy, CaretUp, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";

const PLANS = ["standard"];
const STATUSES = ["active", "pending", "suspended"];

const blankCreate = { org_name: "", admin_name: "", admin_email: "", admin_password: "", plan: "standard", status: "active" };

export default function PlatformOrganizationsPage() {
    const [orgs, setOrgs] = useState([]);
    const [filters, setFilters] = useState({ status: "__all__", plan: "__all__", search: "" });
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [createDraft, setCreateDraft] = useState(blankCreate);
    const [creating, setCreating] = useState(false);

    // table sort + paginate
    const [sortBy, setSortBy] = useState("created_at");
    const [sortDir, setSortDir] = useState(-1);
    const [page, setPage] = useState(1);
    const pageSize = 10;

    const load = async () => {
        try {
            const params = {};
            if (filters.status && filters.status !== "__all__") params.status = filters.status;
            if (filters.plan && filters.plan !== "__all__") params.plan = filters.plan;
            if (filters.search) params.search = filters.search;
            const { data } = await api.get("/platform/organizations", { params });
            setOrgs(data);
        } catch (e) { toast.error(extractError(e)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const openEdit = (o) => { setEditing(o); setDraft({ status: o.status }); };
    const save = async () => {
        try {
            await api.patch(`/platform/organizations/${editing.id}`, draft);
            toast.success("Organization updated");
            setEditing(null); await load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const doDelete = async () => {
        try {
            await api.delete(`/platform/organizations/${deleting.id}`);
            toast.success("Organization deleted");
            setDeleting(null); await load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const submitCreate = async (e) => {
        e?.preventDefault();
        setCreating(true);
        try {
            await api.post("/platform/organizations", createDraft);
            toast.success(`Organization "${createDraft.org_name}" created`);
            setCreateOpen(false); setCreateDraft(blankCreate); await load();
        } catch (e) { toast.error(extractError(e)); }
        finally { setCreating(false); }
    };

    const copySlug = (slug) => {
        const url = `${window.location.origin}/p/${slug}`;
        navigator.clipboard?.writeText(url);
        toast.success("Public org URL copied");
    };

    const sortedOrgs = useMemo(() => {
        const arr = [...orgs].sort((a, b) => {
            const av = a[sortBy] ?? "";
            const bv = b[sortBy] ?? "";
            if (typeof av === "number") return (av - bv) * sortDir;
            return String(av).localeCompare(String(bv)) * sortDir;
        });
        return arr;
    }, [orgs, sortBy, sortDir]);
    const totalPages = Math.max(1, Math.ceil(sortedOrgs.length / pageSize));
    const pageRows = useMemo(() => sortedOrgs.slice((page - 1) * pageSize, page * pageSize), [sortedOrgs, page]);
    const toggleSort = (col) => {
        if (sortBy === col) setSortDir(sortDir === 1 ? -1 : 1);
        else { setSortBy(col); setSortDir(1); }
        setPage(1);
    };
    const SortIcon = ({ col }) =>
        sortBy === col ? (sortDir === 1 ? <CaretUp size={10} className="inline ml-1" /> : <CaretDown size={10} className="inline ml-1" />) : null;

    return (
        <PageContainer>
            <PageHeader
                overline="Platform"
                title="Organizations"
                description="Every tenant on the platform."
                actions={
                    <Button onClick={() => setCreateOpen(true)} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="create-org-btn">
                        <Plus size={16} className="mr-2" /> New organization
                    </Button>
                }
                testid="platform-orgs-header"
            />

            <div className="flex flex-wrap gap-2 mb-6">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search organizations…" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} className="rounded-sm pl-9" data-testid="orgs-search" />
                </div>
                <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                    <SelectTrigger className="w-40 rounded-sm" data-testid="orgs-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All statuses</SelectItem>
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Button variant="outline" className="rounded-sm" onClick={load} data-testid="orgs-apply-filter">Apply</Button>
            </div>

            {orgs.length === 0 ? (
                <EmptyState icon={Buildings} title="No organizations match" testid="orgs-empty" />
            ) : (
                <div className="brut-section overflow-x-auto">
                    <table className="w-full" data-testid="orgs-table">
                        <thead><tr>
                            <th className="data-header text-left cursor-pointer select-none" onClick={() => toggleSort("name")} data-testid="orgs-sort-name">
                                Name<SortIcon col="name" />
                            </th>
                            <th className="data-header text-left">Slug · Public URL</th>
                            <th className="data-header text-left">Primary email</th>
                            <th className="data-header text-left cursor-pointer select-none" onClick={() => toggleSort("status")} data-testid="orgs-sort-status">
                                Status<SortIcon col="status" />
                            </th>
                            <th className="data-header text-left cursor-pointer select-none" onClick={() => toggleSort("user_count")} data-testid="orgs-sort-users">
                                Users<SortIcon col="user_count" />
                            </th>
                            <th className="data-header text-left cursor-pointer select-none" onClick={() => toggleSort("form_count")} data-testid="orgs-sort-forms">
                                Forms<SortIcon col="form_count" />
                            </th>
                            <th className="data-header text-left cursor-pointer select-none" onClick={() => toggleSort("created_at")} data-testid="orgs-sort-created">
                                Created<SortIcon col="created_at" />
                            </th>
                            <th className="data-header"></th>
                        </tr></thead>
                        <tbody>
                            {pageRows.map((o) => (
                                <tr key={o.id} className="hover:bg-secondary/50" data-testid={`org-row-${o.id}`}>
                                    <td className="data-cell font-medium">{o.name}</td>
                                    <td className="data-cell font-mono text-xs text-muted-foreground">
                                        <button onClick={() => copySlug(o.slug)} className="hover:text-foreground inline-flex items-center gap-1" data-testid={`copy-slug-${o.id}`}>
                                            /p/{o.slug} <Copy size={11} />
                                        </button>
                                    </td>
                                    <td className="data-cell font-mono text-xs" data-testid={`org-primary-email-${o.id}`}>
                                        {o.primary_email
                                            ? (<a href={`mailto:${o.primary_email}`} className="hover:underline text-foreground" title={o.primary_admin_name || ""}>{o.primary_email}</a>)
                                            : <span className="text-muted-foreground">—</span>}
                                    </td>
                                    <td className="data-cell">
                                        <Badge className={`rounded-sm font-mono text-[10px] ${o.status === "active" ? "" : o.status === "suspended" ? "bg-destructive" : "bg-yellow-500"}`}>{o.status}</Badge>
                                    </td>
                                    <td className="data-cell font-mono text-sm">{o.user_count}</td>
                                    <td className="data-cell font-mono text-sm">{o.form_count}</td>
                                    <td className="data-cell font-mono text-xs text-muted-foreground">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
                                    <td className="data-cell text-right whitespace-nowrap">
                                        <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => openEdit(o)} data-testid={`edit-org-${o.id}`}>Manage</Button>
                                        <Button variant="ghost" size="sm" className="rounded-sm text-destructive hover:text-destructive" onClick={() => setDeleting(o)} data-testid={`delete-org-${o.id}`}>
                                            <Trash size={14} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="flex items-center justify-between px-4 py-3 border-t border-border text-sm">
                        <div className="text-muted-foreground">Total: {sortedOrgs.length} · Page {page}/{totalPages}</div>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" className="rounded-sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="orgs-prev">Previous</Button>
                            <Button variant="outline" size="sm" className="rounded-sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="orgs-next">Next</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Manage org */}
            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent className="rounded-sm" data-testid="edit-org-dialog">
                    <DialogHeader><DialogTitle>Manage "{editing?.name}"</DialogTitle></DialogHeader>
                    {draft && (
                        <div className="space-y-4">
                            <div>
                                <Label className="label-overline">Status</Label>
                                <Select value={draft.status} onValueChange={(v) => setDraft({ ...draft, status: v })}>
                                    <SelectTrigger className="mt-2 rounded-sm" data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="border border-border bg-secondary rounded-sm p-3 text-xs">
                                <div className="label-overline mb-1">Subscription</div>
                                <p className="text-muted-foreground">Use the Payments tab to manage subscription packs and per-org limits.</p>
                            </div>
                            <DialogFooter><Button onClick={save} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-org-btn">Apply changes</Button></DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Create org offline */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="rounded-sm" data-testid="create-org-dialog">
                    <DialogHeader>
                        <DialogTitle>Create organization (offline)</DialogTitle>
                        <DialogDescription>Provisions a new tenant + initial org admin. No email is sent — share credentials manually.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitCreate} className="space-y-4">
                        <div>
                            <Label className="label-overline">Organization name</Label>
                            <Input required value={createDraft.org_name} onChange={(e) => setCreateDraft({ ...createDraft, org_name: e.target.value })} className="mt-2 rounded-sm" data-testid="create-org-name" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-overline">Admin name</Label>
                                <Input required value={createDraft.admin_name} onChange={(e) => setCreateDraft({ ...createDraft, admin_name: e.target.value })} className="mt-2 rounded-sm" data-testid="create-admin-name" />
                            </div>
                            <div>
                                <Label className="label-overline">Admin email</Label>
                                <Input required type="email" value={createDraft.admin_email} onChange={(e) => setCreateDraft({ ...createDraft, admin_email: e.target.value })} className="mt-2 rounded-sm" data-testid="create-admin-email" />
                            </div>
                        </div>
                        <div>
                            <Label className="label-overline">Initial password (min 6 chars)</Label>
                            <Input required minLength={6} value={createDraft.admin_password} onChange={(e) => setCreateDraft({ ...createDraft, admin_password: e.target.value })} className="mt-2 rounded-sm font-mono" data-testid="create-admin-password" />
                        </div>
                        <div>
                            <Label className="label-overline">Status</Label>
                            <Select value={createDraft.status} onValueChange={(v) => setCreateDraft({ ...createDraft, status: v })}>
                                <SelectTrigger className="mt-2 rounded-sm" data-testid="create-status-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="text-xs text-muted-foreground border border-border rounded-sm p-3 bg-secondary">
                            <strong>Plan:</strong> Standard pack. Quotas and pricing are configured in <code>Platform → Pricing</code>. Add expansion packs from Billing later.
                        </div>
                        <DialogFooter>
                            <Button type="submit" disabled={creating} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="submit-create-org-btn">
                                {creating ? "Creating…" : "Create organization"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation */}
            <Dialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
                <DialogContent className="rounded-sm" data-testid="delete-org-dialog">
                    <DialogHeader>
                        <DialogTitle>Delete "{deleting?.name}"?</DialogTitle>
                        <DialogDescription>
                            This permanently removes the org, all its users, forms, records, groups, roles, settings and audit history.
                            This action <strong>cannot be undone</strong>.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-sm" onClick={() => setDeleting(null)} data-testid="cancel-delete-org">Cancel</Button>
                        <Button className="rounded-sm bg-destructive hover:bg-destructive/90 text-destructive-foreground" onClick={doDelete} data-testid="confirm-delete-org">Delete forever</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
}
